import { type ParentComponent, createContext, useContext, onMount, createEffect, onCleanup } from "solid-js";
import { createStore } from "solid-js/store";
import type { PromptBarContextType, PromptBarProps, ScopeAsset, ScopeConcept } from "../PromptBar.types";
import { useCreditsDisplay, useKnowledgeSourcesDisplay, usePromptInput, usePromptMode } from "./hooks";
import { useWire } from "~/wire";
import { createPromptEditor, type MentionItem, useMentionsState } from "@storytell/rich-text";
import { VariableV1EditorRenderer } from "./components/VariableV1Renderer/VariableV1Renderer";
import type { RichText } from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";

const PromptBarContext = createContext<PromptBarContextType>();

export const PromptBarProvider: ParentComponent<PromptBarProps> = (props) => {
  const input = usePromptInput();
  const mode = usePromptMode();
  const credits = useCreditsDisplay(props.streamingState?.accumulatedMetrics);
  const knowledgeSources = useKnowledgeSourcesDisplay();
  const wire = useWire();

  // Scope management
  const [scopeState, setScopeState] = createStore({
    concepts: [] as ScopeConcept[],
    assets: [] as ScopeAsset[],
  });

  const scope = {
    assets: () => scopeState.assets,
    concepts: () => scopeState.concepts,

    addAsset: (
      assetId: string,
      filename: string,
      status: "uploading" | "processing" | "completed" | "error" = "uploading",
    ) => {
      setScopeState("assets", (prev) => {
        // Check if asset already exists
        const exists = prev.find((asset) => asset.assetId === assetId);
        if (exists) return prev;
        return [...prev, { assetId, filename, status }];
      });
    },
    addConcept: (conceptId: string, name: string) => {
      setScopeState("concepts", (prev) => {
        // Check if asset already exists
        const exists = prev.find((concept) => concept.conceptId === conceptId);
        if (exists) return prev;
        return [...prev, { conceptId, name }];
      });
    },

    updateAssetStatus: (assetId: string, status: "uploading" | "processing" | "completed" | "error") => {
      setScopeState("assets", (prev) =>
        prev.map((asset) => (asset.assetId === assetId ? { ...asset, status } : asset)),
      );
    },

    removeAsset: (assetId: string) => {
      if (scopeState.assets.find((asset) => asset.assetId === assetId)) {
        setScopeState("assets", (prev) => prev.filter((asset) => asset.assetId !== assetId));
        dispatchGlobalEvent("removed-item-from-prompt-scope", {
          id: assetId,
        });
      }
    },

    removeConcept: (conceptId: string) => {
      if (scopeState.concepts.find((concept) => concept.conceptId === conceptId)) {
        setScopeState("concepts", (prev) => prev.filter((concept) => concept.conceptId !== conceptId));
        dispatchGlobalEvent("removed-item-from-prompt-scope", {
          id: conceptId,
        });
      }
    },

    clearAll: () => {
      const oldAssets = scopeState.assets;
      setScopeState("assets", []);
      const oldConcepts = scopeState.concepts;
      setScopeState("concepts", []);
      for (const asset of oldAssets) {
        dispatchGlobalEvent("removed-item-from-prompt-scope", {
          id: asset.assetId,
        });
      }
      for (const concept of oldConcepts) {
        dispatchGlobalEvent("removed-item-from-prompt-scope", {
          id: concept.conceptId,
        });
      }
    },

    toProtobufScope: () => {
      const projectId = wire.auth.realm().projectId;
      const assetIds = scopeState.assets.map((asset) => asset.assetId);
      const conceptIds = scopeState.concepts.map((concept) => concept.conceptId);
      return {
        assetIdList: assetIds,
        conceptIdList: conceptIds,
        projectIdList: assetIds.length + conceptIds.length > 0 ? [] : [projectId],
      };
    },
  };

  const handleSubmit = (text: RichText.RichText) => {
    props.onSubmit({
      text,
      scope: scope.toProtobufScope(),
    });
  };

  const mentionsState = useMentionsState({
    // TODO: @andi: limit how many we're returning here
    getMentionItems: async (query) => {
      if (query === "") {
        return [
          ...wire.sync.concepts
            .getAll()
            .map((concept) => ({ type: "concept" as const, id: concept.id, data: concept, text: concept.name })),
          ...wire.sync.assets
            .getAll()
            .map((asset) => ({ type: "asset" as const, id: asset.id, data: asset, text: asset.data.originalFilename })),
        ];
      }
      const assets = wire.sync.assets.fuzzy.search(query || "").map((result) => ({
        type: "asset" as const,
        id: result.item.id,
        data: result.item,
        text: result.item.data.originalFilename,
      }));
      const concepts = wire.sync.concepts.fuzzy.search(query || "").map((result) => ({
        type: "concept" as const,
        id: result.item.id,
        data: result.item,
        text: result.item.name,
      }));
      return [...concepts, ...assets];
    },
  });

  const promptEditor = createPromptEditor({
    autofocus: () => true,
    mentionsState,
    variableV1Props: {
      renderer: VariableV1EditorRenderer,
    },
    placeholder: () => "What are you trying to accomplish?",
    onSubmit: (text) => {
      handleSubmit(text);
    },
    onInput: (text) => {
      input.setInputValue(text);
    },
  });

  // Connect the prompt bar mentions to the scope
  onMount(() => {
    const unsubscribeAdded = subscribeToGlobalEvent("added-mention-to-prompt-bar", (event) => {
      if (event.detail.id.startsWith("asset_")) {
        const asset = wire.sync.assets.get(event.detail.id);
        if (asset) {
          scope.addAsset(event.detail.id, asset.data.originalFilename, "completed");
        }
      } else if (event.detail.id.startsWith("concept_")) {
        const concept = wire.sync.concepts.get(event.detail.id);
        if (concept) {
          scope.addConcept(event.detail.id, concept.name);
        }
      }
    });
    const unsubscribeRemoved = subscribeToGlobalEvent("removed-mention-from-prompt-bar", (event) => {
      if (event.detail.id.startsWith("asset_")) {
        scope.removeAsset(event.detail.id);
      } else if (event.detail.id.startsWith("concept_")) {
        scope.removeConcept(event.detail.id);
      }
    });
    onCleanup(() => {
      unsubscribeAdded();
      unsubscribeRemoved();
    });
  });

  const contextValue: PromptBarContextType = {
    input: {
      inputValue: input.inputValue,
      setInputValue: input.setInputValue,
      characterCount: input.characterCount,
      lineCount: input.lineCount,
      hasInputText: input.hasInputText,
    },
    prompt: promptEditor,
    mode: {
      selectedMode: mode.selectedMode,
      setSelectedMode: mode.setSelectedMode,
      placeholderText: mode.placeholderText,
    },
    credits: {
      showTooltip: credits.showTooltip,
      showPricingModal: credits.showPricingModal,
      setShowTooltip: credits.setShowTooltip,
      toggleTooltip: credits.toggleTooltip,
      hideTooltip: credits.hideTooltip,
      openPricingModal: credits.openPricingModal,
      closePricingModal: credits.closePricingModal,
      credits: credits.credits,
      dailyCredits: credits.dailyCredits,
      planType: credits.planType,
      totalCredits: credits.totalCredits,
    },
    knowledgeSources: {
      showTooltip: knowledgeSources.showTooltip,
      training: knowledgeSources.training,
      web: knowledgeSources.web,
      myKnowledge: knowledgeSources.myKnowledge,
      memories: knowledgeSources.memories,
      selectedModel: knowledgeSources.selectedModel,
      setShowTooltip: knowledgeSources.setShowTooltip,
      toggleTooltip: knowledgeSources.toggleTooltip,
      hideTooltip: knowledgeSources.hideTooltip,
      toggleTraining: knowledgeSources.toggleTraining,
      toggleWeb: knowledgeSources.toggleWeb,
      toggleMyKnowledge: knowledgeSources.toggleMyKnowledge,
      toggleMemories: knowledgeSources.toggleMemories,
      setSelectedModel: knowledgeSources.setSelectedModel,
    },
    scope,
    submission: {
      handleSubmit,
    },
    streaming: props.streamingState,
  };

  return <PromptBarContext.Provider value={contextValue}>{props.children}</PromptBarContext.Provider>;
};

export const usePromptBarContext = (): PromptBarContextType => {
  const context = useContext(PromptBarContext);
  if (!context) {
    throw new Error("usePromptBarContext must be used within a PromptBarProvider");
  }
  return context;
};
